import React, { useState, useEffect } from 'react';
import { AlertTriangle, Shield, CheckCircle2, ChevronDown, Lock, ShieldAlert, FileSignature } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const LegalDisclaimerModal = () => {
  const { t } = useLanguage(); // Dil fonksiyonunu al
  
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    const hasAccepted = localStorage.getItem('burofuchs_disclaimer_accepted');
    if (!hasAccepted) {
      const timer = setTimeout(() => setIsOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleScroll = (e) => {
    const element = e.target;
    // Tolerans payı (+40px) ile sonuna gelip gelmediğini kontrol et
    const isBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 40;
    if (isBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (!hasScrolledToBottom) return; // Güvenlik önlemi
    const acceptanceData = { accepted: true, timestamp: new Date().toISOString(), version: '1.0' };
    localStorage.setItem('burofuchs_disclaimer_accepted', JSON.stringify(acceptanceData));
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="max-w-xl p-0 gap-0 overflow-hidden border border-white/10 rounded-[2.5rem] bg-[#020617] shadow-[0_0_100px_rgba(0,0,0,0.8)] outline-none text-slate-200 [&>button]:hidden font-sans"
      >
        <DialogTitle className="sr-only">Security Protocol</DialogTitle>

        {/* ELITE HEADER */}
        <header className="bg-[#0f172a] px-8 py-6 flex items-center justify-between relative overflow-hidden border-b border-white/5">
          {/* Background Tech Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
          
          <div className="flex items-center gap-4 relative z-10">
            <div className="p-3 bg-orange-600 rounded-2xl shadow-[0_0_20px_rgba(234,88,12,0.4)]">
              <ShieldAlert size={24} className="text-white animate-pulse" />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tighter uppercase italic leading-none text-white">
                Legal <span className="text-orange-500">Pilot</span>
              </h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mt-1">{t('legal_disclaimer_subtitle') || 'Compliance & Advisory'}</p>
            </div>
          </div>
          <Badge className="bg-white/5 border border-white/10 text-slate-400 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest backdrop-blur-md">v1.0.4</Badge>
        </header>

        {/* CONTENT AREA */}
        <div 
          onScroll={handleScroll}
          className="px-8 py-8 overflow-y-auto max-h-[60vh] custom-scrollbar space-y-8 bg-[#020617]"
        >
          {/* YELLOW ADVISORY BOX */}
          <div className="p-6 rounded-[2rem] bg-amber-500/5 border border-amber-500/20 flex gap-5 items-start shadow-[0_0_30px_rgba(245,158,11,0.05)]">
            <div className="p-2 bg-amber-500/10 rounded-xl text-amber-500 shrink-0 border border-amber-500/20">
              <AlertTriangle size={20} />
            </div>
            <div className="space-y-2">
              <h3 className="font-black text-xs uppercase tracking-widest text-amber-500">
                {t('legal_disclaimer_important') || 'Wichtiger Hinweis'}
              </h3>
              <p className="text-xs font-medium leading-relaxed text-amber-200/80">
                {t('legal_disclaimer_warning') || 'BüroFuchs ist ein KI-gestützter Assistent und kein Ersatz für professionelle steuerliche oder rechtliche Beratung.'}
              </p>
            </div>
          </div>

          {/* MAIN LEGAL TEXT */}
          <div className="space-y-8 text-sm font-medium text-slate-400 leading-relaxed">
            <div className="pl-4 border-l-2 border-orange-500/50">
                <p className="italic">
                {t('legal_disclaimer_main') || 'BüroFuchs nutzt modernste KI-Modelle (Gemini 2.0 Pro), um komplexe deutsche Behördenangelegenheiten zu visualisieren und zu vereinfachen.'}
                </p>
            </div>

            <div className="space-y-4 pt-2">
              <h4 className="text-[10px] font-black text-white uppercase tracking-widest flex items-center gap-2 mb-4">
                <FileSignature size={14} className="text-emerald-500" /> Protocol Agreement
              </h4>
              <ul className="space-y-3">
                {[1, 2, 3].map((point) => (
                  <li key={point} className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors">
                    <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-[#0f172a] border border-white/10 flex items-center justify-center text-[10px] font-black text-orange-500 shadow-inner font-mono">
                      0{point}
                    </span>
                    <span className="text-xs text-slate-300">{t(`legal_disclaimer_point_${point}`) || `Point ${point} content...`}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* INFO BOX */}
            <div className="p-4 rounded-2xl bg-blue-500/5 border border-blue-500/10 flex gap-3 items-center justify-center">
              <Lock size={14} className="text-blue-500" />
              <p className="text-[9px] font-black uppercase tracking-widest text-blue-400">Secure Protocol Encryption Active</p>
            </div>
          </div>

          {/* SCROLL CUE */}
          {!hasScrolledToBottom && (
            <div className="flex flex-col items-center justify-center pt-4 text-orange-500 animate-bounce opacity-80">
              <span className="text-[9px] font-black uppercase tracking-widest mb-1">{t('legal_scroll_unlock') || 'Scroll to unlock'}</span>
              <ChevronDown size={16} />
            </div>
          )}
        </div>

        {/* FOOTER ACTIONS */}
        <footer className="p-8 pt-4 bg-[#0f172a] border-t border-white/5 flex flex-col gap-4 items-center relative z-20">
          <Button 
            onClick={handleAccept} 
            disabled={!hasScrolledToBottom}
            className={`w-full h-16 rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-[10px] shadow-2xl transition-all active:scale-[0.98] border border-white/5 ${
              hasScrolledToBottom 
                ? 'bg-orange-600 hover:bg-orange-500 text-white shadow-orange-900/40 cursor-pointer' 
                : 'bg-white/5 text-slate-500 cursor-not-allowed grayscale'
            }`}
          >
            {hasScrolledToBottom ? <span className="flex items-center gap-2"><CheckCircle2 size={16} /> {t('legal_accept') || 'Accept & Proceed'}</span> : (t('legal_read_continue') || 'Read to Continue')}
          </Button>
          
          <div className="flex items-center gap-2 opacity-30">
            <Shield size={10} className="text-slate-500" />
            <span className="text-[8px] font-black uppercase tracking-[0.4em] text-slate-500 italic">
               BüroFuchs Security Compliance Terminal
            </span>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
};

export default LegalDisclaimerModal;